import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  FC,
} from "react";
import {
  DEFAULT_THEME_PREFERENCE,
  ThemeMode,
  ThemePreference,
} from "@/domains/styling/theme-preference";
import { localStorageModule } from "@/modules/local-storage";

interface ThemePreferenceContextValue {
  preference: ThemePreference;
  resolvedTheme: ThemeMode;
  setPreference: (preference: ThemePreference) => void;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | null>(null);

const prefersDarkMediaQuery = "(prefers-color-scheme: dark)";

const applyResolvedThemeToDocument = (mode: ThemeMode) => {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  root.classList.toggle("atlas-dark-mode", mode === "dark");
  root.style.colorScheme = mode;

  if (document.body) {
    document.body.style.colorScheme = mode;
  }
};

export const ThemePreferenceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreference] = useState<ThemePreference>(DEFAULT_THEME_PREFERENCE);
  const [resolvedTheme, setResolvedTheme] = useState<ThemeMode>("light");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let isActive = true;

    const initializePreference = async () => {
      await localStorageModule.clearThemePreference();

      if (!isActive) {
        return;
      }

      setPreference(DEFAULT_THEME_PREFERENCE);
      setIsLoaded(true);
    };

    void initializePreference();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    void localStorageModule.writeThemePreference(preference);
  }, [isLoaded, preference]);

  useEffect(() => {
    if (typeof window === "undefined") {
      setResolvedTheme("light");
      return;
    }

    const mediaQuery = window.matchMedia(prefersDarkMediaQuery);

    const resolveTheme = (): ThemeMode => {
      if (preference === "system") {
        return mediaQuery.matches ? "dark" : "light";
      }

      return preference;
    };

    const updateResolvedTheme = () => {
      const mode = resolveTheme();
      setResolvedTheme(mode);
      applyResolvedThemeToDocument(mode);
    };

    updateResolvedTheme();

    if (preference !== "system") {
      return;
    }

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateResolvedTheme);
      return () => mediaQuery.removeEventListener("change", updateResolvedTheme);
    }

    mediaQuery.addListener(updateResolvedTheme);
    return () => mediaQuery.removeListener(updateResolvedTheme);
  }, [preference]);

  const updatePreference = useCallback((nextPreference: ThemePreference) => {
    setPreference(nextPreference);
  }, []);

  const value = useMemo<ThemePreferenceContextValue>(
    () => ({
      preference,
      resolvedTheme,
      setPreference: updatePreference,
    }),
    [preference, resolvedTheme, updatePreference]
  );

  return (
    <ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>
  );
};

export const useThemePreference = (): ThemePreferenceContextValue => {
  const context = useContext(ThemePreferenceContext);

  if (!context) {
    throw new Error("useThemePreference must be used within a ThemePreferenceProvider");
  }

  return context;
};
